import React, { HTMLAttributes } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import PageSection from "components/layout/PageSection";
import DefaultAosAnimation from "components/aos/DefaultAosAnimation";
import FeatureTile, {
    IFeature,
    DescriptionBackgroundVariant,
    FeatureTileScribbleVariant,
} from "./featureTile/FeatureTile";

import featuresImage01 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_01.jpg";
import featuresImage02 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_02.jpg";
import featuresImage03 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_03.jpg";
import featuresImage04 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_04.jpg";
import featuresImage05 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_05.jpg";
import featuresImage06 from "assets/images/homePage/gameInfoSection/featuresTile/screenShots/img_features_06.jpg";
import Rotate from "components/rotate/Rotate";
import Spacer from "components/Spacer";
import titleImage from "assets/images/homePage/gameInfoSection/title_gameinfo.png";
import Typography from "@material-ui/core/Typography";
import clsx from "clsx";
import useBackgroundStyles from "components/backgrounds/useBackgroundStyles";
import routes from "routes";

const useStyles = makeStyles((theme: Theme) => {
    const featureTileMargin = theme.spacing(2);

    return createStyles({
        root: {},
        titleContainer: {
            paddingTop: theme.spacing(2),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        descriptionContainer: {
            display: "flex",
            justifyContent: "center",
            color: theme.palette.common.white,
            textAlign: "center",
        },
        description: {
            maxWidth: 975,
            lineHeight: 1.35,
        },
        featureTilesContainer: {
            flex: "1 1 auto",
            [theme.breakpoints.up("sm")]: {
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
            },
        },
        featureTile: {
            margin: featureTileMargin,
            boxSizing: "border-box",
            width: 330,
            "@media (max-width: 800px)": {
                width: 475,
            },
            [theme.breakpoints.down("xs")]: {
                width: "unset",
            },
        },
    });
});

interface IFeatureTile extends IFeature {
    rotateAmount?: number;
    descriptionBackgroundVariant?: DescriptionBackgroundVariant;
    scribbleVariant?: FeatureTileScribbleVariant;
}

const features: IFeatureTile[] = [
    {
        title: "Push The Limits with Incredible Cars",
        description:
            "900bhp sprint cars, incredible rock bouncers, modern rallycross, classic rally, GT, muscle cars, trucks, buggies and more are yours to let loose with.",
        imgSrc: featuresImage01,
        rotateAmount: -1,
        descriptionBackgroundVariant: "v2",
        scribbleVariant: "v1",
        articleRoute: `${routes.news.route}/push-the-limits`,
    },
    {
        title: "Blaze a Trail Across the Globe",
        description:
            "Over 70 routes across 10 global locations - from racing on New York's frozen East River, to under Norway's Northern Lights.",
        imgSrc: featuresImage02,
        rotateAmount: 0,
        descriptionBackgroundVariant: "v3",
        articleRoute: `${routes.news.route}/blaze-a-trail`,
    },
    {
        title: "Exciting and Diverse Game Modes ",
        description:
            "Enjoy four-player split-screen, the all-new Playgrounds mode, a narrative-driven Career, fun multiplayer action and more.",
        imgSrc: featuresImage03,
        rotateAmount: 1,
        descriptionBackgroundVariant: "v1",
        scribbleVariant: "v2",
        articleRoute: `${routes.news.route}/exciting-and-diverse`,
    },
    {
        title: "Own the Spotlight in a Star-Studded Career",
        description:
            "Troy Baker and Nolan North star in DIRT 5's Career mode, featuring choice-driven progression, real-world sponsors and a huge variety of events.",
        imgSrc: featuresImage04,
        rotateAmount: -1,
        descriptionBackgroundVariant: "v2",
        articleRoute: `${routes.news.route}/own-the-spotlight`,
    },
    {
        title: "Create, Share, Discover",
        description:
            "Build up and throw down in Gymkhana, Gate Crasher and Smash Attack events on player-made racing arenas.",
        imgSrc: featuresImage05,
        rotateAmount: 1,
        descriptionBackgroundVariant: "v3",
        scribbleVariant: "v3",
        articleRoute: `${routes.news.route}/create-share-discover`,
    },
    {
        title: "Innovative online racing",
        description:
            "Thrilling races, unlockable rewards and the return of online party modes - DIRT 5's multiplayer action doubles down on fun, exhilarating racing.",
        imgSrc: featuresImage06,
        rotateAmount: -1,
        descriptionBackgroundVariant: "v2",
        articleRoute: `${routes.news.route}/dirt-5-online-multiplayer`,
    },
];

interface IProps extends HTMLAttributes<HTMLDivElement> {}

export default function GameInfoSection(props: IProps) {
    const { className, id, ...rest } = props;
    const classes = useStyles();
    const backgrounds = useBackgroundStyles();

    return (
        <PageSection
            skewed
            topSeparatorVariant="primary"
            className={clsx(className, classes.root)}
            classes={{ skewedSectionRoot: backgrounds.default }}
            {...rest}
        >
            <div>
                <Rotate amount={-1}>
                    <div className={classes.titleContainer} id={id}>
                        <img
                            style={{ maxWidth: 300, width: "100%" }}
                            src={titleImage}
                            alt="Game Info"
                        />
                    </div>

                    <Spacer />

                    <div className={classes.descriptionContainer}>
                        <Typography className={classes.description}>
                            DIRT 5 is the bold new off-road arcade racing experience created by
                            Codemasters. Conquer stunning global routes and drive an iconic roster
                            of cars. Experience a star-studded Career mode, four-player
                            split-screen, the Playgrounds arena creator mode and much more!
                        </Typography>
                    </div>
                </Rotate>

                <Spacer size={2} />

                <div className={classes.featureTilesContainer}>
                    {features.map((feature, i) => {
                        const {
                            rotateAmount = 0,
                            descriptionBackgroundVariant,
                            scribbleVariant,
                            ...rest
                        } = feature;

                        return (
                            <DefaultAosAnimation order={i + 1}>
                                <Rotate amount={rotateAmount}>
                                    <FeatureTile
                                        className={classes.featureTile}
                                        feature={rest}
                                        descriptionBackgroundVariant={descriptionBackgroundVariant}
                                        scribbleVariant={scribbleVariant}
                                    />
                                </Rotate>
                            </DefaultAosAnimation>
                        );
                    })}
                </div>
            </div>
        </PageSection>
    );
}
